import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import KpiCard from "@/components/KpiCard";
import EmailTemplatesPanel from "@/components/EmailTemplatesPanel";
import AnnouncementsPanel from "@/components/AnnouncementsPanel";
import CronHealthWidget from "@/components/CronHealthWidget";
import JourneyRunsWidget from "@/components/JourneyRunsWidget";
import RoadmapDialog from "@/components/RoadmapDialog";
import { useI18n } from "@/lib/i18n";
import { Building2, Users, Banknote, AlertTriangle, MessageSquare, Power, Plus, QrCode, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function SuperAdmin() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("clinics");
  const [addOpen, setAddOpen] = useState(false);

  const loadStats = () => api.get("/super-admin/stats").then((r) => setStats(r.data));
  const loadRequests = () => api.get("/clinic-signup-requests").then((r) => setRequests(r.data));

  useEffect(() => { loadStats(); loadRequests(); }, []);

  const toggle = async (id, name) => {
    try {
      const r = await api.patch(`/clinics/${id}/toggle`);
      toast.success(`${name} ${r.data.is_active ? "activated" : "paused"}`);
      loadStats();
    } catch { toast.error("Toggle failed"); }
  };

  if (!stats) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  const pendingCount = requests.filter(r => r.status !== "approved" && r.status !== "rejected").length;

  return (
    <div className="min-h-screen app-bg pb-16 relative overflow-hidden">
      <RoadmapDialog />
      <div className="blob bg-[#004a99]/10 h-72 w-72 -top-20 -left-10" />
      <div className="blob bg-[#f37021]/10 h-80 w-80 top-40 right-0" style={{ animationDelay: "2s" }} />

      <AppHeader title="Projexino Master Dashboard" subtitle="Multi-tenant control room" badge="Super-Admin" />

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 mt-6 sm:mt-8 relative z-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="superadmin-heading">{t("dash_global_overview")}</h1>
            <p className="text-slate-500 mt-1">{t("dash_global_sub")}</p>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="open-add-clinic-dialog">
                <Plus className="h-4 w-4 mr-2" /> {t("dash_add_clinic")}
              </Button>
            </DialogTrigger>
            <AddClinicDialog onClose={() => { setAddOpen(false); loadStats(); }} />
          </Dialog>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <KpiCard testId="kpi-revenue-recovered" label={t("kpi_revenue")} value={fmtINR(stats.revenue_recovered)} sublabel={`${stats.invoices_recovered} ${t("kpi_invoices", {n: ""}).trim()}`} icon={Banknote} accent="orange" />
          <KpiCard testId="kpi-clinics-active" label={t("tab_clinics")} value={`${stats.active_clinics}/${stats.total_clinics}`} sublabel={t("sa_tenant_network")} icon={Building2} accent="blue" />
          <KpiCard testId="kpi-total-patients" label={t("sa_total_patients")} value={stats.total_patients} sublabel={`${stats.total_users} ${t("sa_platform_users")}`} icon={Users} accent="green" />
          <KpiCard testId="kpi-overdue" label={t("kpi_overdue")} value={stats.overdue_count} sublabel={`${stats.triage_sessions} ${t("sa_triage_sessions")}`} icon={AlertTriangle} accent="slate" />
        </div>

        {/* Cron Health + Journey Runs widgets */}
        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
          <CronHealthWidget />
          <JourneyRunsWidget />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl flex flex-wrap h-auto gap-1 justify-start overflow-x-auto">
            <TabsTrigger value="clinics" data-testid="tab-clinics">{t("tab_clinics")}</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">
              {t("tab_requests")} {pendingCount > 0 && <span className="ml-2 bg-[#f37021] text-white text-xs rounded-full px-2">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">{t("tab_emails")}</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements">{t("tab_announcements")}</TabsTrigger>
          </TabsList>

          <TabsContent value="clinics" className="mt-6">
            <section className="solid-panel p-0 overflow-hidden shadow-3d">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white">
                <div>
                  <h2 className="text-xl font-heading font-semibold">{t("tab_clinics")}</h2>
                  <p className="text-sm text-slate-500">{t("sa_tenant_clinics_sub")}</p>
                </div>
                <span className="tiny-label">{t("sa_tenants_count", { n: stats.clinics.length })}</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("th_clinic")}</TableHead>
                    <TableHead>{t("th_patients")}</TableHead>
                    <TableHead>{t("th_revenue_recovered")}</TableHead>
                    <TableHead>{t("th_status")}</TableHead>
                    <TableHead className="text-right">{t("th_access")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.clinics.map((c) => (
                    <TableRow key={c.clinic_id} data-testid={`clinic-row-${c.clinic_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-lg bg-[#e6f0ff] overflow-hidden flex items-center justify-center">
                            {c.logo_url
                              ? <img src={c.logo_url} alt="" className="h-full w-full object-cover" />
                              : <Building2 className="h-4 w-4 text-[#004a99]" />}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">{c.name}</div>
                            <div className="text-xs text-slate-500">{c.clinic_id}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{c.patients}</TableCell>
                      <TableCell className="font-semibold text-[#004a99]">{fmtINR(c.revenue_recovered)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 ${c.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${c.is_active ? "bg-emerald-500" : "bg-slate-400"}`} />
                          {c.is_active ? t("status_active") : t("status_paused")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={c.is_active}
                          onCheckedChange={() => toggle(c.clinic_id, c.name)}
                          data-testid={`clinic-toggle-${c.clinic_id}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <RequestsPanel requests={requests} onChanged={() => { loadRequests(); loadStats(); }} />
          </TabsContent>

          <TabsContent value="emails" className="mt-6">
            <EmailTemplatesPanel />
          </TabsContent>

          <TabsContent value="announcements" className="mt-6">
            <AnnouncementsPanel />
          </TabsContent>
        </Tabs>

        <section className="mt-8 grid lg:grid-cols-3 gap-5">
          <div className="glass-panel rounded-2xl p-6 lg:col-span-2 shadow-3d">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-heading font-semibold">Why Pulse-Agent works</h3>
              <span className="tiny-label">Value</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { t: "WhatsApp Triage", d: "AI sorts emergencies from routine, 24/7." },
                { t: "Revenue Recovery", d: "Auto outreach to overdue patients." },
                { t: "Voice-to-EMR", d: "From speech to PDF prescription in 8 seconds." },
              ].map((x) => (
                <div key={x.t} className="tilt-card rounded-xl bg-white border border-slate-200 p-4">
                  <div className="text-sm font-semibold text-[#004a99]">{x.t}</div>
                  <div className="text-sm text-slate-600 mt-1">{x.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="tilt-card glass-panel rounded-2xl p-6 shadow-3d">
            <div className="tiny-label">Triage Pipeline</div>
            <div className="kpi-number text-4xl mt-2">{stats.triage_sessions}</div>
            <div className="text-sm text-slate-500 mt-1">Sessions across all tenants</div>
            <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700">
              <MessageSquare className="h-4 w-4" /> Live, multi-tenant
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function RequestsPanel({ requests, onChanged }) {
  const [busy, setBusy] = useState(null);

  const sendQr = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/clinic-signup-requests/${id}/send-payment-qr`);
      toast.success(`Payment QR sent on WhatsApp${r.data.whatsapp?.simulated ? " (simulated)" : ""}`);
      onChanged?.();
    } catch { toast.error("Failed to send QR"); } finally { setBusy(null); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/clinic-signup-requests/${id}/approve`);
      toast.success(`Approved! Temp password: ${r.data.temp_password}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); } finally { setBusy(null); }
  };

  return (
    <section className="solid-panel p-0 overflow-hidden shadow-3d">
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <h2 className="text-xl font-heading font-semibold">Pending clinic signups</h2>
        <p className="text-sm text-slate-500">Send payment QR → mark approved → clinic goes live.</p>
      </div>
      {requests.length === 0 && <div className="p-10 text-center text-slate-400 text-sm">No signup requests yet. They appear here when new clinics fill the public form.</div>}
      <Table>
        <TableHeader>
          <TableRow><TableHead>Clinic</TableHead><TableHead>Contact</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((r) => (
            <TableRow key={r.id} data-testid={`signup-row-${r.id}`}>
              <TableCell>
                <div className="font-semibold">{r.clinic_name}</div>
                <div className="text-xs text-slate-500">{r.address || "—"} · {r.specialties || "—"}</div>
              </TableCell>
              <TableCell>
                <div>{r.contact_name}</div>
                <div className="text-xs text-slate-500">{r.contact_email} · {r.contact_phone}</div>
              </TableCell>
              <TableCell><span className="uppercase text-xs font-bold text-[#004a99]">{r.plan}</span></TableCell>
              <TableCell>
                <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${
                  r.status === "approved" ? "bg-emerald-50 text-emerald-700"
                  : r.status === "qr_sent" ? "bg-amber-50 text-amber-700"
                  : "bg-slate-100 text-slate-600"}`}>{r.status}</span>
              </TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-2">
                  <Button size="sm" variant="outline" disabled={busy === r.id || r.status === "approved"} onClick={() => sendQr(r.id)} data-testid={`send-qr-${r.id}`}>
                    <QrCode className="h-3.5 w-3.5 mr-1" /> Send QR
                  </Button>
                  <Button size="sm" disabled={busy === r.id || r.status === "approved"} onClick={() => approve(r.id)} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid={`approve-${r.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve & Activate
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function AddClinicDialog({ onClose }) {
  const [form, setForm] = useState({
    clinic_name: "", slug: "", logo_url: "", address: "", clinic_phone: "",
    admin_name: "", admin_email: "", admin_password: "", admin_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.clinic_name || !form.admin_email || !form.admin_password) {
      return toast.error("Clinic name, admin email & password required");
    }
    setBusy(true);
    try {
      await api.post("/clinics-with-admin", form);
      toast.success(`${form.clinic_name} created. Admin can log in now.`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="sm:max-w-[640px]" data-testid="add-clinic-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl">Add new clinic</DialogTitle>
        <p className="text-sm text-slate-500">Creates a clinic and its first admin doctor account, instantly.</p>
      </DialogHeader>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="sm:col-span-2 tiny-label text-[#004a99]">Clinic</div>
        <div>
          <Label>Clinic name *</Label>
          <Input value={form.clinic_name} onChange={(e) => set("clinic_name", e.target.value)} placeholder="Apollo Care Clinic" data-testid="add-clinic-name" />
        </div>
        <div>
          <Label>Slug</Label>
          <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))} placeholder="apollo-care" data-testid="add-clinic-slug" />
        </div>
        <div className="sm:col-span-2">
          <Label>Logo URL</Label>
          <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." data-testid="add-clinic-logo" />
        </div>
        <div className="sm:col-span-2">
          <Label>Address</Label>
          <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="add-clinic-address" />
        </div>
        <div>
          <Label>Clinic phone</Label>
          <Input value={form.clinic_phone} onChange={(e) => set("clinic_phone", e.target.value)} data-testid="add-clinic-phone" />
        </div>
        <div></div>

        <div className="sm:col-span-2 tiny-label text-[#f37021] mt-2">First admin doctor</div>
        <div>
          <Label>Admin name *</Label>
          <Input value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} placeholder="Dr. Anya Sharma" data-testid="add-admin-name" />
        </div>
        <div>
          <Label>Admin phone</Label>
          <Input value={form.admin_phone} onChange={(e) => set("admin_phone", e.target.value)} placeholder="+9198..." data-testid="add-admin-phone" />
        </div>
        <div>
          <Label>Admin email *</Label>
          <Input type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} data-testid="add-admin-email" />
        </div>
        <div>
          <Label>Temp password *</Label>
          <Input value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)} data-testid="add-admin-password" />
        </div>
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="add-clinic-submit">
          {busy ? "Creating…" : "Create clinic"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
